import React from 'react';
import { Link } from 'react-router-dom';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import './MarketingFooter.css';

/**
 * MarketingFooter — the standard footer for the logged-out marketing shell.
 *
 * A full multi-column footer that links every public marketing page. Beyond UX,
 * this is the site's primary INTERNAL-LINKING surface: it gives crawlers a path
 * to every marketing URL from any page (important while the marketing routes are
 * client-rendered). Part of the Marketing Page Standard — every public page
 * rendered inside MarketingLayout gets this footer automatically.
 */
const YEAR = new Date().getFullYear();

const COLUMNS = [
  {
    // Column 1. Order ruled by Nitish (Aug 24 2026, eve): Product |
    // Integrations | AI Employees | Compare | Company — five flat columns,
    // the earlier Product+Integrations stacked rail is retired.
    title: 'Product',
    links: [
      { label: 'AI Employees', to: '/ai-employees' },
      { label: 'AI Employees in Meetings', to: '/ai-employees/meetings' },
      { label: 'Super-Agents', to: '/super-agents' },
      { label: 'Agent-to-Agent', to: '/agent-to-agent' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Tutorials', to: '/tutorials' },
      { label: 'Podcast', to: '/podcast' },
      { label: 'Skills', to: '/skills' },
      { label: 'For AI agents', to: '/for-agents' },
    ],
  },
  {
    // Column 2 (promoted from the retired rail — Nitish, Aug 24 2026 eve).
    title: 'Integrations',
    links: [
      { label: 'Linear', to: '/linear' },
      { label: 'OpenClaw', to: '/openclaw' },
      { label: 'Hermes', to: '/hermes' },
      { label: 'Cursor', to: '/cursor' },
      { label: 'Claude Code', to: '/claude-code' },
      { label: 'Codex', to: '/codex' },
    ],
  },
  {
    // The primary internal-linking surface for the programmatic-SEO role
    // pages (fast indexing; see agent_docs/ai_employee_role_seo_pages_prd.md).
    title: 'AI Employees',
    // The long one (23 role links). Rendered as a two-sub-column directory on
    // desktop and a full-width band on phone — layout only, never a trim: this
    // list is the internal-linking surface those pages index from.
    roles: true,
    links: [
      { label: 'AI Executive Assistant', to: '/ai-employees/ai-executive-assistant' },
      { label: 'AI Marketing Manager', to: '/ai-employees/ai-marketing-manager' },
      { label: 'AI Project Manager', to: '/ai-employees/ai-project-manager' },
      { label: 'AI Research Assistant', to: '/ai-employees/ai-research-assistant' },
      { label: 'AI Content Writer', to: '/ai-employees/ai-content-writer' },
      { label: 'AI SEO Specialist', to: '/ai-employees/ai-seo-specialist' },
      { label: 'AI Social Media Manager', to: '/ai-employees/ai-social-media-manager' },
      { label: 'AI Community Manager', to: '/ai-employees/ai-community-manager' },
      { label: 'AI Legal Assistant', to: '/ai-employees/ai-legal-assistant' },
      { label: 'AI Receptionist', to: '/ai-employees/ai-receptionist' },
      { label: 'AI SDR', to: '/ai-employees/ai-sdr' },
      { label: 'AI Customer Support Agent', to: '/ai-employees/ai-customer-support-agent' },
      { label: 'AI Data Analyst', to: '/ai-employees/ai-data-analyst' },
      { label: 'AI Bookkeeper', to: '/ai-employees/ai-bookkeeper' },
      { label: 'AI Recruiter', to: '/ai-employees/ai-recruiter' },
      { label: 'AI Virtual Assistant', to: '/ai-employees/ai-virtual-assistant' },
      { label: 'AI Operations Manager', to: '/ai-employees/ai-operations-manager' },
      { label: 'AI Software Engineer', to: '/ai-employees/ai-software-engineer' },
      { label: 'AI Designer', to: '/ai-employees/ai-designer' },
      { label: 'AI Chief of Staff', to: '/ai-employees/ai-chief-of-staff' },
      { label: 'AI Content Creator', to: '/ai-employees/ai-content-creator' },
      { label: 'AI Head of Growth', to: '/ai-employees/ai-head-of-growth' },
      { label: 'For Home Services', to: '/ai-employees/for-home-services' },
      { label: 'For Real Estate Teams', to: '/ai-employees/for-real-estate-teams' },
      { label: 'For Shopify Stores', to: '/ai-employees/for-shopify-stores' },
    ],
  },
  {
    // Curation rule (agreed with Nitish, Jul 22 2026 — two criteria merged):
    //  1. STRATEGIC PEERS (his call): the platforms we actively position
    //     against — Aidan, Viktor, OpenClaw, Hermes.
    //  2. DEMONSTRATED SEARCH DEMAND (GSC 90d, re-check quarterly): top
    //     alternatives pages by impressions (sintra 83, lindy 18).
    //  3. RHEA'S RULING (Aug 25 2026): direct slots only for buyer-query
    //     alternatives pages with real volume (grok-bot-alternatives) and
    //     money-category pages (ai-sdr-platforms); all other vs/alternatives
    //     pages get their crawl path via the /compare hub. Never expand this
    //     block toward the full roster (footer-stuffing dilutes PageRank).
    // Plus the hub, both category pages, and benchmarks. The /compare hub
    // carries the FULL roster (auto-derived from compareConfigs.js) — new
    // pages go in the config, never here.
    title: 'Compare',
    links: [
      { label: 'All Comparisons', to: '/compare' },
      { label: 'Best AI Employee Platforms', to: '/compare/ai-employee-platforms' },
      { label: 'Best AI SDR Platforms', to: '/compare/ai-sdr-platforms' },
      { label: 'Best Deep Research AI', to: '/compare/best-deep-research-ai' },
      { label: 'Benchmarks', to: '/benchmarks' },
      { label: 'CellCog vs Grok Bot', to: '/compare/cellcog-vs-grok-bot' },
      { label: 'Grok Bot Alternatives', to: '/compare/grok-bot-alternatives' },
      { label: 'CellCog vs Aidan', to: '/compare/cellcog-vs-aidan' },
      { label: 'CellCog vs Viktor', to: '/compare/cellcog-vs-viktor' },
      { label: 'CellCog vs OpenClaw', to: '/compare/cellcog-vs-openclaw' },
      { label: 'CellCog vs Hermes Agent', to: '/compare/cellcog-vs-hermes-agent' },
      { label: 'Sintra Alternatives', to: '/compare/sintra-alternatives' },
      { label: 'Lindy Alternatives', to: '/compare/lindy-alternatives' },
    ],
  },
  {
    title: 'Company',
    links: [
      // external: true -> plain <a> full page load (static nginx-served blog,
      // not a React route — same convention as the header NAV).
      { label: 'Blog', to: '/blog/', external: true },
      { label: 'The CellCog Story', to: '/story' },
      { label: 'The AI Organization', to: '/ai-organization' },
      { label: 'Contact', to: '/contact' },
      { label: 'Community Posts', to: '/posts' },
      { label: 'Support Docs', to: '/support' },
      { label: 'Security', to: '/security' },
      { label: 'Privacy Policy', to: '/support/privacy-policy' },
      { label: 'Terms of Service', to: '/support/terms-of-service' },
    ],
  },
];

/**
 * One footer column = its title + its link list — the single markup path for
 * every column, so the desktop and phone shapes are a CSS reflow of this same
 * DOM, never a second render branch.
 */
const renderColumn = (col) => (
  <div
    key={col.title}
    className={`mkt-footer__col${col.roles ? ' mkt-footer__col--roles' : ''}`}
  >
    <span className="mkt-footer__col-title">{col.title}</span>
    <nav className="mkt-footer__col-links">
      {col.links.map((l) =>
        l.external ? (
          <a key={l.to} href={l.to} className="mkt-footer__link">
            {l.label}
          </a>
        ) : (
          <Link key={l.to} to={l.to} className="mkt-footer__link">
            {l.label}
          </Link>
        )
      )}
    </nav>
  </div>
);

const MarketingFooter = () => {
  // Geo-split v3 (Aug 24): most visitors see NO banner — this footer control
  // (+ the profile Privacy & Data section) is their path to the full consent
  // panel, satisfying the notice + working-opt-out model (Figma/Cursor pattern).
  const { openSettings } = useCookieConsent();
  return (
  <footer className="mkt-footer">
    <div className="mkt-footer__inner">
      <div className="mkt-footer__top">
        <div className="mkt-footer__brand">
          <Link to="/" className="mkt-footer__logo">CellCog</Link>
          <p className="mkt-footer__tagline">
            Hire AI employees for any role. You build the org chart. They
            manage each other.
          </p>
        </div>
        <div className="mkt-footer__cols">
          {/* Five flat columns in source order (Nitish, Aug 24 2026):
              Product | Integrations | AI Employees | Compare | Company.
              Desktop spreads them edge-to-edge (the blank-space fix); the
              phone shape is unchanged — the roles directory still reflows to
              the end via CSS order. One markup path, every viewport. */}
          {COLUMNS.map(renderColumn)}
        </div>
      </div>
      <div className="mkt-footer__bottom">
        <span className="mkt-footer__copy">© {YEAR} CellCog</span>
        <button
          type="button"
          className="mkt-footer__cookie-btn"
          onClick={openSettings}
        >
          Cookie preferences
        </button>
      </div>
    </div>
  </footer>
  );
};

export default MarketingFooter;
